import { Button, Descriptions, Flex, Space } from "antd";
import Titulo, { EnumTituloTipo } from "@/Models/Financeiro/Titulo/Titulo";
import TituloService from "@/Services/Financeiro/Titulo/TituloService";
import { Ordenar } from "@/Utils/Ordenacao";
import Utilitario from "@/Utils/Utilitario";
import Typography from "antd/es/typography/Typography";
import TablePaginadaBasic, { ITablePaginadaBasicProps } from "@/Components/Base/Table/TablePaginadaBasic";
import { IPagina, IPaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { SettingOutlined } from "@ant-design/icons";
import TituloTableParcela from "./TituloTableParcela";
import { UseQueryResult } from "@tanstack/react-query";
import OpceosTitulo from "./OpceosTitulo";

type Dummy = {};

export interface ITituloTableFiltros { 
    titulo?: Titulo;
    ativo?: boolean;
    pagina?:IPaginaInfo;
    receita?:boolean;
}

interface ITituloTableProps extends ITablePaginadaBasicProps<Titulo, Dummy> {
    isDesativar?: boolean,
    isAtivar?: boolean,
    isPagar?: boolean,
    isVisualizar?: boolean;
    onEditar?: (titulo: Titulo) => void,
    onVisualizar?: (titulo: Titulo) => void,
    filtros:[ITituloTableFiltros|undefined, React.Dispatch<React.SetStateAction<ITituloTableFiltros|undefined>>],
    queryResult:UseQueryResult<IPagina<Titulo>, Error>;
}

export default function TituloTable({
    isDesativar,
    isAtivar,
    isPagar,
    isVisualizar,
    onEditar,
    onVisualizar,
    filtros : [filtros, setFiltros],
    queryResult : queryResultParam,
    ...rest
}: ITituloTableProps) {
    isDesativar = isDesativar ?? true;
    isAtivar = isAtivar ?? true;
    isPagar = isPagar ?? true;
    isVisualizar = isVisualizar ?? true;

    const {
        data,
        isLoading,
        isFetching,
        refetch
    } = queryResultParam;

    const {
        mutate: mutateDesativar,
        isPending: isPedingDesativar
    } = TituloService.useDesativar({
        onSuccess() {
            refetch();
        }
    });

    const {
        mutate: mutateAtivar,
        isPending: isPedingAtivar
    } = TituloService.useAtivar({
        onSuccess() {
            refetch();
        }
    });

    const {
        mutate: mutateGerarLink,
        isPending: isPedingGerarLink
    } = TituloService.useGerarLink({

    });

    const haLinhasSelecionadas = (rest?.rowSelection?.selectedRowKeys?.length ?? 0) > 0;
    const allSelecionados = (rest?.rowSelection?.selectedRowKeys?.length ?? 0) >= (data?.registros.length ?? 0);

    return <Flex gap="middle" vertical>
        {
            haLinhasSelecionadas && <Flex vertical>
                <Typography>
                    Quantidade de itens selecionados {(rest?.rowSelection?.selectedRowKeys?.length ?? 0)}
                </Typography>
                {
                    <Button
                        style={{ width: "min-content" }}
                        onClick={() => {
                            if (allSelecionados)
                                rest?.rowSelection?.onChange?.([], [], { type: "all" });
                            else 
                                rest?.rowSelection?.onChange?.(data?.registros.map(m => m.codigo) ?? [], data?.registros ?? [], { type: "all" });
                        }}
                        type="dashed"
                        size="small"
                    >
                        {allSelecionados && "Limpar seleção" || "Selecionar todos"}
                    </Button>
                }
            </Flex>
        }
        
        <TablePaginadaBasic<Titulo<any>, Dummy>
            {...rest}
            bordered
            locale={{ emptyText: 'Nenhum titulo encontrado.' }}
            dataSource={data}
            disableResponsiveness={true}
            tableLayout="auto"
            scroll={{
                x: true,
            }}
            showHeader={true}
            minColWidthIfResponsivenessDisabled={"120px"}
            loading={isLoading || isFetching || isPedingAtivar || isPedingDesativar}
            rowKey={(k) => k.codigo}
            onChange={(pagina) => {
                setFiltros?.({
                    pagina : {
                        pagina : pagina.current ?? 1,
                        tamanho : pagina.pageSize ?? 15
                    } as IPaginaInfo
                });
            }}
            expandable={{
                expandedRowRender : (record) => (
                    record.codTipo === EnumTituloTipo.Titulo 
                        ? <TituloTableParcela codFatura={record.codigo} /> 
                        : (
                            record.codFatura !== undefined && record.codFatura > 0 && record.fatura !== undefined 
                                ? <Descriptions 
                                    title="Título" 
                                    extra={<OpceosTitulo
                                        isDesativar={isDesativar}
                                        isAtivar={isAtivar}
                                        isPagar={isPagar}
                                        isVisualizar={isVisualizar}
                                        onEditar={(titulo: Titulo) => onEditar?.(titulo)}
                                        onSucess={() => refetch()}
                                        titulo={record.fatura}
                                        children={<Button type="primary" size="small">Opções</Button>}
                                    />} 
                                    items={[
                                        {
                                            key: 'reembolsavel',
                                            label: 'Reembolsavel',
                                            children: record?.fatura?.reembolsavel ? "Sim" : "Não"
                                        },
                                        (record?.fatura?.comprovante?.length ?? 0) === 0 ? undefined : {
                                            key: 'comprovante',
                                            label: 'Comprovante',
                                            children: record?.fatura?.comprovante
                                        },
                                        (record?.fatura?.urlComprovante?.length ?? 0) === 0 ? undefined : {
                                            key: 'urlComprovante',
                                            label: 'Url Comprovante',
                                            children: record?.fatura?.urlComprovante
                                        },
                                        {
                                            key: 'data-emissao',
                                            label: 'Dt. Emissão',
                                            children: record?.fatura?.dataEmissao.toLocaleDateString(),
                                        },
                                        {
                                            key: 'valor-base',
                                            label: 'Vl. Base',
                                            children: Utilitario.toStringDinheiro(record?.fatura?.valorBase)
                                        }
                                    ].filter(Boolean)} 
                                />
                                : undefined
                        )
                )
            }}
            columns={[
                {
                    title: "ID",
                    dataIndex: "codigo",
                    sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                    key: "codigo",
                    minWidth : "70px"
                },
                {
                    title: "Tipo",
                    dataIndex: "codTipo",
                    render: (codTipo, _) => codTipo === EnumTituloTipo.Titulo ? "Título" : "Parcela",
                    key: "codTipo",
                    minWidth : "70px"
                },
                {
                    title: "Título",
                    dataIndex: "codFatura",
                    render: (codFatura, _) => codFatura ?? ' - ',
                    key: "codTipo",
                    minWidth : "70px"
                },
                filtros?.receita == true ? undefined : {
                    title: "Beneficiário",
                    dataIndex: ["beneficiario", "nome"],
                    render: (_, titulo) => titulo.beneficiario?.nomeSocial == null ? titulo.beneficiario?.nome : titulo.beneficiario?.nomeSocial,
                    sorter: (titulo1, titulo2) => Ordenar.String(titulo1.beneficiario?.nomeSocial == null ? titulo1.beneficiario?.nome : titulo1.beneficiario?.nomeSocial, titulo2.beneficiario?.nomeSocial == null ? titulo2.beneficiario?.nome : titulo2.beneficiario?.nomeSocial),
                    key: "beneficiario",
                    minWidth : "200px"
                },
                filtros?.receita == false ? undefined : {
                    title: "Devedor",
                    dataIndex: ["devedor", "nome"],
                    render: (_, titulo) => titulo.devedor?.nomeSocial == null ? titulo.devedor?.nome : titulo.devedor?.nomeSocial,
                    sorter: (titulo1, titulo2) => Ordenar.String(titulo1.devedor?.nomeSocial == null ? titulo1.devedor?.nome : titulo1.devedor?.nomeSocial, titulo2.devedor?.nomeSocial == null ? titulo2.devedor?.nome : titulo2.devedor?.nomeSocial),
                    key: "devedor",
                    minWidth : "200px"
                },
                {
                    title: "Vencimento",
                    dataIndex: "dataVencimento",
                    render: (value: Date, titulo:Titulo) => titulo.codTipo === EnumTituloTipo.Parcela ? value.toLocaleDateString() : '',
                    sorter: (titulo1, titulo2) => Ordenar.Data(titulo1.dataVencimento, titulo2.dataVencimento),
                    key: "dataVencimento",
                    breakpoints : { xs : 12, sm : 6, md : 5, lg : 5, xl : 3, xxl : 3 }
                },
                {
                    title: "Valor Prev.",
                    dataIndex: "valorPrevisto",
                    render: (value: number, titulo:Titulo) => Utilitario.toStringDinheiro(value),
                    sorter: (titulo1, titulo2) => Ordenar.Numero(titulo1.valorPrevisto, titulo2.valorPrevisto),
                    key: "valorPrevisto",
                    breakpoints : { xs : 12, sm : 6, md : 4, lg : 4, xl : 3, xxl : 3 }
                },
                {
                    title: "Situação",
                    dataIndex: ["situacao", "nome"],
                    render : (value:string|undefined) => value ?? ' - ', 
                    sorter: (a, b) => Ordenar.Numero(a.situacao?.ordem, b.codigo),
                    key: "situacao",
                    minWidth : "100px"
                },
                {
                    key: "acoes",
                    title: "Ações",
                    fixed: "right",
                    minWidth : "10px",
                    render: (_, titulo) => <Space size="small" wrap style={{ justifyContent : "center" }}>
                        <OpceosTitulo
                            isDesativar={isDesativar}
                            isAtivar={isAtivar}
                            isPagar={isPagar}
                            isVisualizar={isVisualizar}
                            onEditar={(titulo: Titulo) => onEditar?.(titulo)}
                            onSucess={() => refetch()}
                            titulo={titulo}
                            children={<SettingOutlined />}
                        />
                    </Space>
                }
            ].filter(f => f !== undefined)}
        />
    </Flex>;
}